import React, { useState, useEffect } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { Layers, Search, Loader2 } from 'lucide-react';

interface Category {
  name: string;
  product_count: number;
}

interface CategoryPickerProps {
  siteId: string;
  onCategorySelect: (category: string) => void;
  selectedCategory?: string | null;
}

export default function CategoryPicker({ siteId, onCategorySelect, selectedCategory }: CategoryPickerProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, [siteId]);

  const fetchCategories = async () => {
    if (!siteId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `${API_URL}/growth/categories?siteId=${siteId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.status}`);
      }

      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Loading categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchCategories}
          className="mt-2 text-sm text-blue-600 hover:text-blue-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Layers className="h-12 w-12 mx-auto mb-3 text-gray-400" />
        <p>No categories found on this site.</p>
        <p className="text-sm mt-1">Categories are detected from product pages.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
        {filteredCategories.map((category) => (
          <button
            key={category.name}
            onClick={() => onCategorySelect(category.name)}
            className={`text-left p-4 border rounded-lg hover:shadow-md transition-all ${
              selectedCategory === category.name
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                <Layers className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-500">
                  {category.product_count} products
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {filteredCategories.length === 0 && searchTerm && (
        <p className="text-center text-gray-500 py-4">
          No categories match your search
        </p>
      )}
    </div>
  );
}